import axios from 'axios';
import type { TFunction } from 'i18next';
import type { LoginFailureReason } from '@/helpers/analyticsEvents';

/**
 * Login failures come back as plain-text bodies from RecordLoginsMiddleware (429 lockout,
 * 403 wrong password / unknown email). Without this mapping the user sees axios's raw
 * "Request failed with status code 429", which explains nothing (user complaint from
 * 2026-08-25). Returns a translated, human-readable explanation instead.
 */
const LOGIN_ERROR_I18N: Record<LoginFailureReason, string> = {
  network: 'loginErrors.network',
  too_many_attempts: 'loginErrors.tooManyAttempts',
  server: 'loginErrors.server',
  wrong_password: 'loginErrors.wrongPassword',
  email_not_found: 'loginErrors.emailNotFound',
  generic: 'loginErrors.generic',
};

export const getLoginFailureReason = (err: unknown): LoginFailureReason => {
  if (axios.isAxiosError(err)) {
    if (!err.response) return 'network';
    const { status } = err.response;
    const body = typeof err.response.data === 'string' ? err.response.data : '';
    if (status === 429) return 'too_many_attempts';
    if (status >= 500) return 'server';
    if (status === 403 && body.includes('Incorrect password')) return 'wrong_password';
    if (status === 403 && /Email .* does not exist/.test(body)) return 'email_not_found';
  }
  return 'generic';
};

export const getLoginErrorMessage = (err: unknown, t: TFunction): string => {
  return t(LOGIN_ERROR_I18N[getLoginFailureReason(err)]);
};
